import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, TrendingDown, AlertTriangle, DollarSign, Users,
  Clock, Target, Bell, ChevronRight, Download, FileText,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

/* ═══ MOCK DATA ═══ */
const SPEND_TREND = [
  { mese: 'Gen', corrente: 420, precedente: 390 },
  { mese: 'Feb', corrente: 480, precedente: 410 },
  { mese: 'Mar', corrente: 510, precedente: 450 },
  { mese: 'Apr', corrente: 475, precedente: 470 },
  { mese: 'Mag', corrente: 530, precedente: 490 },
  { mese: 'Giu', corrente: 560, precedente: 510 },
  { mese: 'Lug', corrente: 540, precedente: 530 },
  { mese: 'Ago', corrente: 490, precedente: 500 },
  { mese: 'Set', corrente: 580, precedente: 520 },
  { mese: 'Ott', corrente: 610, precedente: 540 },
  { mese: 'Nov', corrente: null, precedente: 560 },
  { mese: 'Dic', corrente: null, precedente: 580 },
];

const ALERTS = [
  { id: 1, tipo: 'budget', testo: 'Coop. Salute Roma ha utilizzato il 92% del budget annuo', severity: 'critica', tempo: '2h fa' },
  { id: 2, tipo: 'sla', testo: 'Tempo medio attivazione Distretto 3 superiore a 72h (SLA: 48h)', severity: 'alta', tempo: '4h fa' },
  { id: 3, tipo: 'flussi', testo: 'Anomalia flussi CIG: 12 prestazioni senza codice tracciabilità', severity: 'media', tempo: '6h fa' },
  { id: 4, tipo: 'pnrr', testo: 'Obiettivo PNRR Distretto 1: copertura al 7.2% (target 10%)', severity: 'media', tempo: '1g fa' },
  { id: 5, tipo: 'fatture', testo: '18 fatture in scadenza entro 30gg — sollecito pagamento', severity: 'alta', tempo: '1g fa' },
];

const SEV_STYLE = {
  critica: { bg: 'bg-red-50 dark:bg-red-950/20', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  alta: { bg: 'bg-orange-50 dark:bg-orange-950/20', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500' },
  media: { bg: 'bg-amber-50 dark:bg-amber-950/20', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
};

const KPI_DATA = [
  { label: 'Budget Utilizzato YTD', value: '€ 5.2M', target: '€ 7.8M', pct: 66.7, semaforo: 'verde', sub: '66.7% del budget annuo', icon: DollarSign },
  { label: 'Copertura PNRR Over-65', value: '8.4%', target: '10%', pct: 84, semaforo: 'giallo', sub: 'Target DM77: 10% entro 2026', icon: Target },
  { label: 'Costo Medio / Paziente', value: '€ 3.420', target: '€ 3.200', pct: 106.9, semaforo: 'rosso', sub: '+6.9% vs benchmark regionale', icon: Users },
  { label: 'Tempo Medio Attivazione', value: '38h', target: '48h', pct: 79, semaforo: 'verde', sub: 'SLA rispettato (< 48h)', icon: Clock },
];

const SEMAFORO_STYLE = {
  verde: { bg: 'bg-emerald-50 dark:bg-emerald-950/20', border: 'border-emerald-200 dark:border-emerald-800/40', accent: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
  giallo: { bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-200 dark:border-amber-800/40', accent: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
  rosso: { bg: 'bg-red-50 dark:bg-red-950/20', border: 'border-red-200 dark:border-red-800/40', accent: 'text-red-600 dark:text-red-400', dot: 'bg-red-500' },
};

export default function ASLControlTower() {
  return (
    <AppLayout role="asl">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="asl-control-tower">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Control Tower</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Executive Dashboard — Governance ADI Territoriale</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-slate-800 dark:bg-slate-700 hover:bg-slate-900 dark:hover:bg-slate-600 text-white text-xs font-semibold rounded-lg transition-colors" data-testid="btn-genera-report">
            <Download className="w-3.5 h-3.5" /> Genera Report Direzionale
          </button>
        </div>

        {/* KPI Semaforo */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-semaforo">
          {KPI_DATA.map(kpi => {
            const s = SEMAFORO_STYLE[kpi.semaforo];
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className={`${s.bg} rounded-2xl border ${s.border} p-5`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider leading-tight">{kpi.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                    <Icon className={`w-4 h-4 ${s.accent}`} />
                  </div>
                </div>
                <p className={`text-2xl font-bold ${s.accent}`}>{kpi.value}</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Target: {kpi.target} — {kpi.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Main Content: Chart + Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Trend Spesa */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="trend-spesa">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-slate-800 dark:text-white">Andamento Spesa Mensile</h2>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Anno corrente vs anno precedente (K€)</p>
              </div>
              <div className="flex items-center gap-4 text-[10px]">
                <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-blue-500 rounded" /> 2026</div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-slate-300 rounded" /> 2025</div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={SPEND_TREND} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" />
                  <XAxis dataKey="mese" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  <Line type="monotone" dataKey="precedente" name="2025" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} connectNulls />
                  <Line type="monotone" dataKey="corrente" name="2026" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Alert Center */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="alert-center">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-red-500" />
                <h2 className="text-sm font-bold text-slate-800 dark:text-white">Alert Center</h2>
              </div>
              <Badge className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-0 text-[10px] font-bold">{ALERTS.length}</Badge>
            </div>
            <div className="flex flex-col gap-0 max-h-[320px] overflow-y-auto">
              {ALERTS.map(a => {
                const sv = SEV_STYLE[a.severity];
                return (
                  <div key={a.id} className={`px-5 py-3.5 border-b border-slate-100 dark:border-slate-800/60 last:border-b-0 ${sv.bg}`} data-testid={`alert-${a.id}`}>
                    <div className="flex items-start gap-2.5">
                      <span className={`w-2 h-2 rounded-full ${sv.dot} mt-1.5 shrink-0`} />
                      <div className="flex-1">
                        <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">{a.testo}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{a.tempo}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
